import React, { ChangeEvent, useMemo, useState } from 'react';
import { Button } from '../../ui';

import style from './style.module.scss';
import buttonStyles from '../../ui/Button/style.module.scss';

export type UploadBoxProps = {
    uploadDocument: (data: FormData) => Promise<void>;
    buttonLabel: string;
    uploadingLabel?: string;
} & React.ButtonHTMLAttributes<HTMLButtonElement>;

type CombinedProps = React.PropsWithChildren & UploadBoxProps;

const FileUploadButton = ({ children, uploadDocument, buttonLabel, uploadingLabel, disabled: forcedDisabled, ...props }: CombinedProps) => {
    const [uploading, setUploading] = useState(false);
    const disabled = useMemo(() => uploading || forcedDisabled, [forcedDisabled, uploading]);

    const hiddenFileInput = React.useRef<HTMLInputElement>(null);

    const handleClick = () => {
        if (disabled || !hiddenFileInput || hiddenFileInput?.current === null) {
            return;
        }
        hiddenFileInput.current.click();
    };

    const uploadFiles = (e: ChangeEvent<HTMLInputElement>) => {
        if (!e || !e.target || !e.target.files) {
            return;
        }
        const data = new FormData();
        for (const file of e.target.files) {
            data.append('uploadedFiles', file, file.name);
        }
        setUploading(true);
        uploadDocument(data).then(
            () => setUploading(false),
            () => setUploading(false)
        );
    };

    return (
        <>
            {children}
            <input
                onChange={uploadFiles}
                ref={hiddenFileInput}
                type="file"
                className={style.fileInput}
                multiple
                accept="application/pdf,image/tiff,image/gif,image/jpeg,image/png"
            />
            <Button
                uiType="primary"
                disabled={disabled}
                onClick={handleClick}
                id="fileSelect"
                type="button"
                {...props}
                className={uploading ? buttonStyles.executing : undefined}
            >
                {uploading ? uploadingLabel || `${buttonLabel}...` : buttonLabel}
            </Button>
        </>
    );
};

export default FileUploadButton;
